import { CoreProviderFieldDescriptor, CoreProviderFieldOption } from '@core/api/core';

export interface ProviderFieldGroup {
  key: string;
  label: string;
  fields: CoreProviderFieldDescriptor[];
}

export interface ProviderFieldValueChange {
  field: CoreProviderFieldDescriptor;
  value: unknown;
}

export function groupProviderFields(
  fields: CoreProviderFieldDescriptor[],
): ProviderFieldGroup[] {
  const groups = new Map<string, ProviderFieldGroup>();
  for (const field of fields) {
    const key = field.group ?? 'filters';
    const existing = groups.get(key);
    if (existing) {
      existing.fields.push(field);
      continue;
    }

    groups.set(key, {
      key,
      label: key.replace(/_/g, ' '),
      fields: [field],
    });
  }
  return Array.from(groups.values());
}

export function defaultProviderFieldValue(field: CoreProviderFieldDescriptor): unknown {
  switch (field.type) {
    case 'multiselect':
      return [];
    case 'boolean':
      return null;
    case 'number':
      return null;
    case 'date':
    case 'select':
    case 'text':
      return '';
    default: {
      const exhaustive: never = field.type;
      return exhaustive;
    }
  }
}

export function isProviderFieldValueActive(
  field: CoreProviderFieldDescriptor,
  value: unknown,
): boolean {
  switch (field.type) {
    case 'multiselect':
      return Array.isArray(value) && value.length > 0;
    case 'boolean':
      return typeof value === 'boolean';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'date':
    case 'select':
    case 'text':
      return typeof value === 'string' && value.trim() !== '';
    default: {
      const exhaustive: never = field.type;
      return exhaustive;
    }
  }
}

export function normalizeProviderFieldValue(
  field: CoreProviderFieldDescriptor,
  value: unknown,
): unknown {
  switch (field.type) {
    case 'multiselect':
      if (Array.isArray(value)) {
        return value.map(item => coerceProviderOptionValue(item)).filter(item => item !== '');
      }
      if (typeof value === 'string' && value.trim() !== '') {
        return [value];
      }
      return [];
    case 'boolean':
      return typeof value === 'boolean' ? value : null;
    case 'number':
      return typeof value === 'number' ? value : null;
    case 'date':
    case 'select':
    case 'text':
      return typeof value === 'string' ? value : value == null ? '' : String(value);
    default: {
      const exhaustive: never = field.type;
      return exhaustive;
    }
  }
}

/** Selected filter values as strings (for option matching / display). */
export function selectedProviderFieldOptionValues(
  field: CoreProviderFieldDescriptor,
  value: unknown,
): string[] {
  switch (field.type) {
    case 'multiselect': {
      const normalized = normalizeProviderFieldValue(field, value);
      return Array.isArray(normalized) ? normalized.map(String) : [];
    }
    case 'select':
    case 'text':
    case 'date': {
      const normalized = normalizeProviderFieldValue(field, value);
      return typeof normalized === 'string' && normalized.trim() !== '' ? [normalized] : [];
    }
    case 'boolean':
    case 'number':
      return [];
    default: {
      const exhaustive: never = field.type;
      return exhaustive;
    }
  }
}

/** Dynamic fields that should fetch options immediately (core filters + applied values). */
export function shouldPreloadProviderFieldOptions(
  field: CoreProviderFieldDescriptor,
  value: unknown,
): boolean {
  return (
    field.valuesSource === 'dynamic' &&
    (field.group === 'core_filters' || isProviderFieldValueActive(field, value))
  );
}

/**
 * Ensure saved/applied selections appear in the option list (with API labels when known).
 * Selected options are listed first so applied filters stay visible without scrolling.
 */
export function mergeProviderFieldOptionsWithSelected(
  options: CoreProviderFieldOption[],
  selectedValues: string[],
): CoreProviderFieldOption[] {
  if (selectedValues.length === 0) {
    return options;
  }

  const byValue = new Map<string, CoreProviderFieldOption>();
  for (const option of options) {
    byValue.set(String(option.value), option);
  }

  const selectedOptions: CoreProviderFieldOption[] = [];
  for (const selected of selectedValues) {
    const key = String(selected);
    selectedOptions.push(byValue.get(key) ?? { value: key, label: key });
    byValue.delete(key);
  }

  return [...selectedOptions, ...byValue.values()];
}

function coerceProviderOptionValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (isOptionValueObject(value)) {
    return String(value.value ?? '');
  }
  return '';
}

function isOptionValueObject(value: unknown): value is { value: unknown } {
  return typeof value === 'object' && value !== null && 'value' in value;
}

export function buildProviderFiltersPayload(
  fields: CoreProviderFieldDescriptor[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of fields) {
    const value = values[field.name];
    if (!isProviderFieldValueActive(field, value)) {
      continue;
    }

    if (typeof value === 'string') {
      payload[field.name] = value.trim();
    } else {
      payload[field.name] = value;
    }
  }
  return payload;
}

/**
 * Which filter fields a folder should show.
 *
 * Saved `enabledFilters` can be a stale subset (e.g. only Search Text from
 * before main taxonomies were validated). The provider field list is the
 * source of truth — always expose every field available now so Document
 * Status / Type / Product appear once they exist on the tenant.
 */
export function resolveEnabledProviderFilterNames(
  fields: CoreProviderFieldDescriptor[],
  _options: {
    enabledFilters?: string[] | null;
    importFilters?: Record<string, unknown> | null;
  } = {},
): string[] {
  return fields.map(field => field.name);
}

/**
 * Merge folder-scoped filter descriptors onto the provider-wide list.
 * Keeps provider order and fills in any fields the folder endpoint omitted
 * (stale enabledFilters subsets).
 */
export function mergeProviderFilterFieldLists(
  providerFields: CoreProviderFieldDescriptor[],
  folderFields: CoreProviderFieldDescriptor[],
): CoreProviderFieldDescriptor[] {
  if (folderFields.length === 0) {
    return providerFields;
  }

  const folderByName = new Map(folderFields.map(field => [field.name, field]));
  const providerNames = new Set(providerFields.map(field => field.name));
  const merged = providerFields.map(field => folderByName.get(field.name) ?? field);

  for (const field of folderFields) {
    if (!providerNames.has(field.name)) {
      merged.push(field);
    }
  }

  return merged;
}
